import { ArrowRight, FileImage, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PersonCard } from "@/components/PersonCard";
import { openPdf } from "@/lib/api";

const TYPES = ["Matrimonio", "Cerimonia", "Compleanno", "Aziendale", "Altro"];
const CONSUMER = "Persona fisica per finalità private (consumatore)";

export function personSlots(data) {
  if (data.evento_tipo === "Matrimonio") return [["id1", "Sposo", true], ["id2", "Sposa", true]];
  if (data.evento_tipo === "Compleanno" && data.fest_minore === "Sì") return [["id1", "Genitore / tutore (firmatario)", true], ["fest", "Festeggiato minorenne", false]];
  return [["id1", "Cliente / firmatario", false], ["id2", "Secondo committente", false]];
}

const full = (data, p) => `${data[`${p}_nome`] || ""} ${data[`${p}_cognome`] || ""}`.trim();
const filled = (data, p) => Object.keys(data).some((k) => k.startsWith(`${p}_`) && k !== `${p}_relazione` && data[k] && (!Array.isArray(data[k]) || data[k].length));

export function syncToScheda(data) {
  const out = { ...data };
  if (filled(data, "id1")) {
    out.cliente_nome = full(data, "id1");
    out.cliente_cf = data.id1_cf || "";
    if (data.id1_indirizzo) out.cliente_indirizzo = data.id1_indirizzo;
  }
  const useId2 = data.evento_tipo === "Matrimonio" || (!(data.evento_tipo === "Compleanno" && data.fest_minore === "Sì") && filled(data, "id2"));
  out.cliente2_nome = useId2 ? full(data, "id2") : "";
  out.cliente2_cf = useId2 ? data.id2_cf || "" : "";
  if (["Matrimonio", "Compleanno"].includes(data.evento_tipo) && !out.cliente_qualifica) out.cliente_qualifica = CONSUMER;
  return out;
}

const Pills = ({ options, value, onChange, disabled, testId }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((o, i) => (
      <button key={o} type="button" disabled={disabled} data-testid={`${testId}-${i}`} onClick={() => onChange(o)}
        className={`px-5 py-2.5 rounded-full border text-sm transition-colors ${value === o ? "bg-[#0B6E8F] text-white border-[#0B6E8F]" : "bg-white border-[#D4E6EC] hover:border-[#0B6E8F]"}`}>{o}</button>
    ))}
  </div>
);

export function IdentityStep({ cid, data, setFields, update, editable, tpl, onContinue }) {
  const slots = personSlots(data);
  const minor = data.evento_tipo === "Compleanno" && data.fest_minore === "Sì";
  const optionalId2 = slots.find((s) => s[0] === "id2" && !s[2]);
  const showId2 = optionalId2 && (data.show_id2 || filled(data, "id2"));
  const visible = slots.filter((s) => s[0] !== "id2" || s[2] || showId2);

  return (
    <div className="space-y-6">
      <section className="rounded-xl border border-[#D4E6EC] bg-white p-5 sm:p-7 space-y-4 fade-up" data-testid="event-type-step">
        <div className="text-xs uppercase tracking-[0.22em] text-[#7B929C]">Fase 1</div>
        <h3 className="font-serif-it text-3xl font-semibold">Che tipo di evento è?</h3>
        <Pills options={TYPES} value={data.evento_tipo} onChange={(v) => setFields({ evento_tipo: v })} disabled={!editable} testId="event-type-opt" />
        {data.evento_tipo === "Matrimonio" && (
          <p className="text-sm text-[#47606B] flex gap-2"><Info size={16} className="shrink-0 mt-0.5 text-[#0B6E8F]" /> Entrambi gli sposi sono committenti: servono i documenti di entrambi ed entrambi firmeranno ogni foglio del contratto.</p>
        )}
        {data.evento_tipo === "Compleanno" && (
          <div className="space-y-2 pt-2">
            <div className="text-sm font-medium">Il festeggiato è minorenne?</div>
            <Pills options={["Sì", "No"]} value={data.fest_minore} onChange={(v) => setFields({ fest_minore: v })} disabled={!editable} testId="fest-minore-opt" />
            {minor && <p className="text-sm text-[#47606B] flex gap-2"><Info size={16} className="shrink-0 mt-0.5 text-[#0B6E8F]" /> Un minore non può firmare il contratto: firma un genitore o tutore, identificato con documento. Il minore viene indicato come festeggiato.</p>}
          </div>
        )}
      </section>

      {data.evento_tipo && visible.map((s) => <PersonCard key={s[0]} cid={cid} slot={s} data={data} setFields={setFields} editable={editable} tpl={tpl} slotLabels={Object.fromEntries(slots.map((x) => [x[0], x[1]]))} />)}

      {data.evento_tipo && visible.some((s) => (data[`${s[0]}_files`] || []).length) && (
        <section className="rounded-xl border border-[#D4E6EC] bg-white p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="identity-sheet">
          <div>
            <div className="font-medium">{data.evento_tipo === "Matrimonio" ? "Documenti degli sposi su un unico foglio A4" : "Documenti su un unico foglio A4"}</div>
            <p className="text-xs text-[#47606B]">Fronte, retro e codice fiscale di tutti, in bianco e nero effetto fotocopia, pronti da stampare e allegare. Gli originali a colori restano conservati con la loro impronta digitale.</p>
          </div>
          <Button variant="outline" className="rounded-full shrink-0" onClick={() => openPdf(`/contracts/${cid}/identity-sheet`)} data-testid="identity-sheet-open"><FileImage size={15} className="mr-2" /> Apri foglio A4</Button>
        </section>
      )}

      {data.evento_tipo && minor && (
        <section className="rounded-xl border border-[#D4E6EC] bg-white p-5 sm:p-7 space-y-3" data-testid="minor-declaration">
          <div className="text-sm font-medium">Il firmatario agisce in qualità di <span className="text-[#0B6E8F]">*</span></div>
          <Pills options={tpl.relazioni} value={data.id1_relazione} onChange={(v) => setFields({ id1_relazione: v })} disabled={!editable} testId="relazione-opt" />
          <p className="text-xs italic text-[#47606B]">{tpl.minor_declaration}</p>
        </section>
      )}

      {data.evento_tipo && optionalId2 && !showId2 && editable && (
        <Button variant="outline" className="rounded-full" onClick={() => setFields({ show_id2: true })} data-testid="add-second-person">Aggiungi secondo committente</Button>
      )}

      {data.evento_tipo && editable && (
        <Button className="rounded-full h-11 px-6" onClick={() => { update(syncToScheda(data)); onContinue(); }} data-testid="identity-continue-button">
          Riporta i dati nella Scheda e continua <ArrowRight size={16} className="ml-2" />
        </Button>
      )}
    </div>
  );
}
